import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { DayPicker } from 'react-day-picker';
import { format, addMinutes, isBefore } from 'date-fns';
import { PageLayout } from '@/components/layout/PageLayout';
import { cn } from '@/lib/utils';
import { Loader2, AlertCircle, Clock, Calendar, User } from 'lucide-react';
import 'react-day-picker/style.css';

const GET_SCHEDULE_FOR_SELF_SERVICE = gql`
  query GetScheduleForSelfService($scheduleId: ID!) {
    getScheduleForSelfService(scheduleId: $scheduleId) {
      scheduleId
      testName
      testDescription
      duration
      candidateFirstName
      candidateLastName
      scheduledDate
      timeZone
      status
    }
  }
`;

const SELF_SCHEDULE_TEST = gql`
  mutation SelfScheduleTest($input: SelfScheduleInput!) {
    selfScheduleTest(input: $input) {
      success
      schedule {
        scheduleId
        scheduledDate
        timeZone
        status
      }
      message
    }
  }
`;

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Kolkata',
  'Australia/Sydney',
  'Pacific/Auckland',
] as const;

function getTimezoneLabel(tz: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    });
    const parts = formatter.formatToParts(now);
    const tzAbbr = parts.find(p => p.type === 'timeZoneName')?.value || '';
    return `${tz.replace(/_/g, ' ')} (${tzAbbr})`;
  } catch {
    return tz;
  }
}

function detectTimezone(): string {
  try {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (TIMEZONES.includes(detected as typeof TIMEZONES[number])) {
      return detected;
    }
  } catch {
    // Fall through to default
  }
  return 'America/New_York';
}

function generateHourOptions(): number[] {
  return Array.from({ length: 24 }, (_, i) => i);
}

function generateMinuteOptions(): number[] {
  return [0, 15, 30, 45];
}

function formatHour(hour: number): string {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour} ${period}`;
}

function formatMinute(minute: number): string {
  return minute.toString().padStart(2, '0');
}

export function SelfSchedule() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const scheduleId = searchParams.get('id') || '';

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedHour, setSelectedHour] = useState<number>(9);
  const [selectedMinute, setSelectedMinute] = useState<number>(0);
  const [selectedTimezone, setSelectedTimezone] = useState<string>(detectTimezone);
  const [formError, setFormError] = useState<string | null>(null);

  const { data, loading, error } = useQuery(GET_SCHEDULE_FOR_SELF_SERVICE, {
    variables: { scheduleId },
    skip: !scheduleId,
  });

  const [selfSchedule, { loading: scheduling }] = useMutation(SELF_SCHEDULE_TEST);

  const schedule = data?.getScheduleForSelfService;

  // Pre-fill from existing scheduled date
  useEffect(() => {
    if (schedule?.scheduledDate) {
      const existing = new Date(schedule.scheduledDate);
      setSelectedDate(existing);
      setSelectedHour(existing.getHours());
      setSelectedMinute(existing.getMinutes());
    }
    if (schedule?.timeZone) {
      setSelectedTimezone(schedule.timeZone);
    }
  }, [schedule]);

  const scheduledDateTime = useMemo(() => {
    if (!selectedDate) return null;
    const dt = new Date(selectedDate);
    dt.setHours(selectedHour, selectedMinute, 0, 0);
    return dt;
  }, [selectedDate, selectedHour, selectedMinute]);

  const isDateValid = useMemo(() => {
    if (!scheduledDateTime) return false;
    return isBefore(addMinutes(new Date(), 10), scheduledDateTime);
  }, [scheduledDateTime]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!scheduledDateTime || !isDateValid) {
      setFormError('Please select a date and time at least 10 minutes in the future.');
      return;
    }

    try {
      const result = await selfSchedule({
        variables: {
          input: {
            scheduleId,
            scheduledDate: scheduledDateTime.toISOString(),
            timeZone: selectedTimezone,
          },
        },
      });

      const scheduleResult = result.data?.selfScheduleTest;

      if (scheduleResult?.success) {
        const params = new URLSearchParams();
        if (scheduleResult.schedule?.scheduledDate) {
          params.set('date', scheduleResult.schedule.scheduledDate);
        }
        if (scheduleResult.schedule?.timeZone) {
          params.set('tz', scheduleResult.schedule.timeZone);
        }
        navigate(`/schedule/confirmed?${params.toString()}`);
      } else {
        setFormError(scheduleResult?.message || 'Failed to schedule test. Please try again.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setFormError(message);
    }
  };

  if (!scheduleId) {
    return (
      <PageLayout>
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Invalid Scheduling Link</h2>
          <p className="text-muted-foreground">
            This scheduling link is missing required parameters. Please use the link provided in your email.
          </p>
        </div>
      </PageLayout>
    );
  }

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </PageLayout>
    );
  }

  if (error || !schedule) {
    return (
      <PageLayout>
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Schedule Not Found</h2>
          <p className="text-muted-foreground">
            Unable to load schedule details. Please verify your scheduling link.
          </p>
        </div>
      </PageLayout>
    );
  }

  // Status-dependent views
  if (schedule.status === 'IN_PROGRESS') {
    return (
      <PageLayout>
        <div className="text-center py-12 space-y-4">
          <Clock className="h-12 w-12 text-blue-500 mx-auto" />
          <h2 className="text-xl font-semibold">Test In Progress</h2>
          <p className="text-muted-foreground">
            Your test is currently in progress. Good luck!
          </p>
        </div>
      </PageLayout>
    );
  }

  if (schedule.status === 'COMPLETED') {
    return (
      <PageLayout>
        <div className="text-center py-12 space-y-4">
          <Calendar className="h-12 w-12 text-green-500 mx-auto" />
          <h2 className="text-xl font-semibold">Test Completed</h2>
          <p className="text-muted-foreground">
            Your test has been completed. Thank you for your participation.
          </p>
        </div>
      </PageLayout>
    );
  }

  if (schedule.status === 'CANCELLED') {
    return (
      <PageLayout>
        <div className="text-center py-12 space-y-4">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
          <h2 className="text-xl font-semibold">Test Cancelled</h2>
          <p className="text-muted-foreground">
            This test has been cancelled. Please contact the recruiting team for more information.
          </p>
        </div>
      </PageLayout>
    );
  }

  // DRAFT or SCHEDULED: Show scheduling form
  return (
    <PageLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Schedule Your Test</h2>
          <p className="text-muted-foreground mt-1">
            Select a date and time for your technical assessment.
          </p>
        </div>

        {/* Schedule info */}
        <div className="rounded-lg border bg-card p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="font-semibold">{schedule.testName}</span>
          </div>
          <p className="text-sm text-muted-foreground">{schedule.testDescription}</p>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {schedule.duration} minutes
            </span>
            <span className="flex items-center gap-1">
              <User className="h-3.5 w-3.5" />
              {schedule.candidateFirstName} {schedule.candidateLastName}
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Timezone selector */}
          <div className="space-y-2">
            <label htmlFor="timezone" className="text-sm font-medium leading-none">
              Timezone
            </label>
            <select
              id="timezone"
              value={selectedTimezone}
              onChange={(e) => setSelectedTimezone(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {getTimezoneLabel(tz)}
                </option>
              ))}
            </select>
          </div>

          {/* Date picker */}
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none">Date</label>
            <div className="rounded-lg border bg-card p-4 flex justify-center">
              <DayPicker
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={{ before: new Date() }}
              />
            </div>
          </div>

          {/* Time picker */}
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none">Time</label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label htmlFor="hour" className="text-xs text-muted-foreground">
                  Hour
                </label>
                <select
                  id="hour"
                  value={selectedHour}
                  onChange={(e) => setSelectedHour(Number(e.target.value))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  {generateHourOptions().map((hour) => (
                    <option key={hour} value={hour}>
                      {formatHour(hour)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label htmlFor="minute" className="text-xs text-muted-foreground">
                  Minute
                </label>
                <select
                  id="minute"
                  value={selectedMinute}
                  onChange={(e) => setSelectedMinute(Number(e.target.value))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  {generateMinuteOptions().map((minute) => (
                    <option key={minute} value={minute}>
                      {formatMinute(minute)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Selected datetime preview */}
          {scheduledDateTime && (
            <div className={cn(
              "rounded-md border p-3 text-sm",
              isDateValid
                ? "border-green-200 bg-green-50 text-green-800"
                : "border-destructive/50 bg-destructive/10 text-destructive"
            )}>
              {isDateValid ? (
                <p>
                  Selected: {format(scheduledDateTime, 'EEEE, MMMM d, yyyy')} at{' '}
                  {format(scheduledDateTime, 'h:mm a')} ({selectedTimezone.replace(/_/g, ' ')})
                </p>
              ) : (
                <p>
                  Selected time must be at least 10 minutes in the future.
                </p>
              )}
            </div>
          )}

          {formError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
              <p className="text-sm text-destructive">{formError}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={scheduling || !selectedDate || !isDateValid}
            className={cn(
              "inline-flex items-center justify-center w-full h-10 px-4 py-2 rounded-md text-sm font-medium",
              "bg-primary text-primary-foreground hover:bg-primary/90",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
              "disabled:pointer-events-none disabled:opacity-50"
            )}
          >
            {scheduling ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Scheduling...
              </>
            ) : (
              'Confirm Schedule'
            )}
          </button>
        </form>
      </div>
    </PageLayout>
  );
}
